import { useEffect, useState } from "react";
import { FileText, Image as ImageIcon, Package, Plus, Download, Upload, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import { AdminHeader } from "@/components/admin/AdminShared";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { formatDateShort } from "@/lib/helpers";

const MATERIAL_CATS = ["Logo", "Press Kit", "Infografis", "Bahan Publikasi", "Materi"];

export default function MediaAdmin() {
  const [press, setPress] = useState([]);
  const [media, setMedia] = useState([]);
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({ title: "", category: "Materi", file: null });
  const [delItem, setDelItem] = useState(null);

  const confirmDelete = async () => {
    const url = delItem.kind === "press" ? `/press-releases/${delItem.id}` : `/media-assets/${delItem.id}`;
    try {
      await api.delete(url);
      toast.success(`"${delItem.title}" dihapus`);
      setDelItem(null);
      load();
    } catch {
      toast.error("Gagal menghapus");
    }
  };

  const load = () => {
    api.get("/press-releases").then((r) => setPress(r.data));
    api.get("/media-assets").then((r) => setMedia(r.data));
  };
  useEffect(() => { load(); }, []);

  const upload = async () => {
    if (!form.title.trim() || !form.file) { toast.error("Judul dan berkas wajib diisi"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("title", form.title);
      fd.append("category", form.category);
      fd.append("file", form.file);
      await api.post("/media-assets/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Materi berhasil diunggah");
      setOpen(false); setForm({ title: "", category: "Materi", file: null }); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal mengunggah materi");
    } finally { setUploading(false); }
  };

  const photos = media.filter((m) => m.type === "photo");
  const materials = media.filter((m) => m.type === "material");

  return (
    <div>
      <AdminHeader title="Media Center" description="Kelola press release, dokumentasi, dan materi publikasi."
        action={<Button onClick={() => setOpen(true)} className="bg-brandred hover:bg-brandred-700 text-white" data-testid="upload-material-btn"><Plus className="h-4 w-4 mr-1.5" /> Unggah Materi</Button>} />
      <Tabs defaultValue="press">
        <TabsList className="bg-surface">
          <TabsTrigger value="press">Press Release ({press.length})</TabsTrigger>
          <TabsTrigger value="foto">Dokumentasi ({photos.length})</TabsTrigger>
          <TabsTrigger value="materi">Materi ({materials.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="press" className="mt-5 space-y-3">
          {press.map((p) => (
            <div key={p.id} className="bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-3" data-testid={`press-admin-${p.id}`}>
              <div className="h-10 w-10 rounded-lg bg-brandred/10 text-brandred flex items-center justify-center"><FileText className="h-5 w-5" /></div>
              <div className="flex-1 min-w-0"><h4 className="font-bold text-navy text-sm line-clamp-1">{p.title}</h4><div className="text-xs text-slate-400">{p.category} · {formatDateShort(p.date)}</div></div>
              <Button size="icon" variant="ghost" className="text-brandred hover:text-brandred shrink-0" onClick={() => setDelItem({ id: p.id, kind: "press", title: p.title })} data-testid={`delete-press-${p.id}`}><Trash2 className="h-4 w-4" /></Button>
            </div>
          ))}
        </TabsContent>
        <TabsContent value="foto" className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-3">
          {photos.map((p) => (
            <div key={p.id} className="relative group rounded-lg overflow-hidden border border-slate-200" data-testid={`photo-admin-${p.id}`}>
              <img src={p.url} alt={p.title} className="h-32 w-full object-cover" />
              <button onClick={() => setDelItem({ id: p.id, kind: "media", title: p.title })}
                className="absolute top-2 right-2 h-8 w-8 rounded-full bg-white/90 text-brandred flex items-center justify-center shadow hover:bg-brandred hover:text-white transition-colors opacity-0 group-hover:opacity-100"
                data-testid={`delete-photo-${p.id}`} aria-label="Hapus dokumentasi">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </TabsContent>
        <TabsContent value="materi" className="mt-5 grid sm:grid-cols-2 gap-3">
          {materials.map((m) => (
            <div key={m.id} className="bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-3" data-testid={`material-${m.id}`}>
              <div className="h-10 w-10 rounded-lg bg-navy/5 text-navy flex items-center justify-center"><Package className="h-5 w-5" /></div>
              <div className="flex-1 min-w-0"><h4 className="font-bold text-navy text-sm">{m.title}</h4><div className="text-xs text-slate-400 font-mono">{m.file_name}</div></div>
              {m.storage_path ? (
                <a href={`${API}/files/${m.storage_path}`} download data-testid={`download-material-${m.id}`}>
                  <Button size="sm" variant="outline"><Download className="h-4 w-4" /></Button>
                </a>
              ) : (
                <Button size="sm" variant="outline" disabled title="Belum ada berkas"><Download className="h-4 w-4" /></Button>
              )}
              <Button size="sm" variant="ghost" className="text-brandred hover:text-brandred shrink-0" onClick={() => setDelItem({ id: m.id, kind: "media", title: m.title })} data-testid={`delete-material-${m.id}`}><Trash2 className="h-4 w-4" /></Button>
            </div>
          ))}
        </TabsContent>
      </Tabs>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Unggah Materi Publikasi</DialogTitle><DialogDescription>Berkas akan tersimpan aman di object storage dan dapat diunduh publik.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div><Label>Judul Materi</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5" data-testid="material-title" placeholder="Contoh: Logo Bawaslu (PNG)" /></div>
            <div><Label>Kategori</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="mt-1.5" data-testid="material-category"><SelectValue /></SelectTrigger>
                <SelectContent>{MATERIAL_CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Berkas (PDF, JPG, PNG, ZIP — maks. 5MB)</Label>
              <Input type="file" accept=".pdf,.jpg,.jpeg,.png,.zip" className="mt-1.5" data-testid="material-file" onChange={(e) => setForm({ ...form, file: e.target.files?.[0] || null })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
            <Button onClick={upload} disabled={uploading} className="bg-brandred hover:bg-brandred-700 text-white" data-testid="material-upload-submit">
              <Upload className="h-4 w-4 mr-1.5" /> {uploading ? "Mengunggah…" : "Unggah"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <AlertDialog open={!!delItem} onOpenChange={(o) => !o && setDelItem(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus {delItem?.kind === "press" ? "press release" : "unggahan"} ini?</AlertDialogTitle>
            <AlertDialogDescription>"{delItem?.title}" akan dihapus permanen dan tidak lagi tampil di Media Center publik.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Batal</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-brandred hover:bg-brandred-700" data-testid="confirm-delete-media">Hapus</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
